import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { useDefaultLayout } from "react-resizable-panels";
import { CloseIcon } from "@/components/flashcards/Icons";
import { Button } from "@/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { screen, useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

/**
 * Fixed size for the Lernkarten modal. Fixed rather than content-driven, so
 * the dialog doesn't jump while pane content changes. Both dimensions grow
 * with the viewport, but each stops short of filling the screen: deck cards
 * don't fill a 4K window, and the empty space reads as a bug.
 */
export const SPLIT_MODAL_CLASS_NAME =
  "h-[min(calc(100vh-56px),90rem)] w-full max-w-[min(160rem,90vw)]";

/**
 * Code-quiz overlays fill the viewport. `sm:max-h-none` beats the overlay's
 * `sm:max-h-[calc(100vh-56px)]`, which would otherwise keep the inset.
 */
export const CODE_QUIZ_MODAL_CLASS_NAME =
  "h-full max-h-none w-full max-w-none rounded-none sm:max-h-none";

export interface ModalSplitResizable {
  /** Storage key the learner's divider position is remembered under. */
  id: string;
  /** Starting width of the aside pane, e.g. "40%". */
  defaultAsideSize: string;
  /** Neither pane may be dragged narrower than this. */
  minPaneSize: string;
}

interface ModalSplitLayoutProps {
  /** Left pane on the white ground: navigation or the task description. */
  aside: ReactNode;
  /** Right pane on the tinted ground: the work area. */
  main: ReactNode;
  /** Bar pinned beneath both panes. */
  footer?: ReactNode;
  /**
   * Makes the divider draggable on desktop (lg+); below lg the panes stack in
   * the modal's own scroll. Without it the aside takes `asideClassName`'s width
   * and the panes stack with CSS alone.
   */
  resizable?: ModalSplitResizable;
  /** Stacked: show the main pane above the aside (e.g. an editor first). */
  mainFirstWhenStacked?: boolean;
  asideClassName?: string;
  mainClassName?: string;
}

const ASIDE_PANE_CLASS = "flex min-h-0 min-w-0 flex-col bg-background";
const MAIN_PANE_CLASS = "flex min-h-0 min-w-0 flex-col bg-primary/5";

const StaticSplit = ({
  aside,
  main,
  footer,
  mainFirstWhenStacked,
  asideClassName,
  mainClassName,
}: Omit<ModalSplitLayoutProps, "resizable">) => (
  <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div
        data-slot="modal-split-aside"
        className={cn(
          ASIDE_PANE_CLASS,
          "w-full shrink-0 lg:h-full",
          asideClassName
        )}
      >
        {aside}
      </div>
      <div
        data-slot="modal-split-main"
        className={cn(
          MAIN_PANE_CLASS,
          "flex-1",
          mainFirstWhenStacked && "-order-1 lg:order-none",
          mainClassName
        )}
      >
        {main}
      </div>
    </div>
    {footer}
  </div>
);

/**
 * Desktop: a draggable split whose divider position is remembered per learner.
 * Below lg: the panes stack. The two layouts are separate trees picked by a
 * media query rather than one tree toggled with CSS — rendering both would
 * mount the pane content (e.g. CodeMirror) twice.
 */
const ResizableSplit = ({
  resizable,
  aside,
  main,
  footer,
  mainFirstWhenStacked,
  asideClassName,
  mainClassName,
}: ModalSplitLayoutProps & { resizable: ModalSplitResizable }) => {
  const isLargeScreen = useMediaQuery(screen.lg);
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: resizable.id,
    storage: localStorage,
    onlySaveAfterUserInteractions: true,
  });

  const asidePane = (
    <div
      data-slot="modal-split-aside"
      className={cn(
        ASIDE_PANE_CLASS,
        isLargeScreen && "h-full",
        asideClassName
      )}
    >
      {aside}
    </div>
  );
  const mainPane = (
    <div
      data-slot="modal-split-main"
      className={cn(MAIN_PANE_CLASS, isLargeScreen && "h-full", mainClassName)}
    >
      {main}
    </div>
  );

  if (!isLargeScreen) {
    return (
      <div data-testid="modal-split-stacked" className="flex flex-col">
        {mainFirstWhenStacked ? mainPane : asidePane}
        {mainFirstWhenStacked ? asidePane : mainPane}
        {footer}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The wrapper carries the test id: `Group` stamps its own generated id
          onto `data-testid`, so one set on it would be overwritten. */}
      <div data-testid="modal-split-resizable" className="flex min-h-0 flex-1">
        <ResizablePanelGroup
          orientation="horizontal"
          defaultLayout={defaultLayout}
          onLayoutChanged={onLayoutChanged}
        >
          <ResizablePanel
            id="split-aside"
            className="min-w-0"
            defaultSize={resizable.defaultAsideSize}
            minSize={resizable.minPaneSize}
          >
            {asidePane}
          </ResizablePanel>

          <ResizableHandle
            withHandle
            className="bg-border transition-colors hover:bg-primary/40 data-[dragging]:bg-primary"
          />

          <ResizablePanel
            id="split-main"
            className="min-w-0"
            minSize={resizable.minPaneSize}
          >
            {mainPane}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      {footer}
    </div>
  );
};

/**
 * Two-pane modal body in the Lernkarten style: white aside on the left, tinted
 * work area on the right, optional footer bar beneath both. Pair it with
 * `SPLIT_MODAL_CLASS_NAME` on the modal, `ModalSplitTitle` at the top of the
 * aside and `ModalSplitCloseButton` in the modal's corner.
 */
export const ModalSplitLayout = ({
  resizable,
  ...props
}: ModalSplitLayoutProps) =>
  resizable ? (
    <ResizableSplit resizable={resizable} {...props} />
  ) : (
    <StaticSplit {...props} />
  );

/**
 * Modal heading at the top of the aside, matching the Lernkarten title. Long
 * titles wrap — and break mid-word where a single word is too long — rather
 * than being cut off, so the learner always sees which task they are in.
 */
export const ModalSplitTitle = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <h2
    // `text-heading-h2` stays outside `cn`: tailwind-merge reads the custom
    // utility as a text colour and would drop it in favour of `text-primary`.
    className={`text-heading-h2 ${cn(
      "min-w-0 break-words leading-tight text-primary",
      className
    )}`}
  >
    {children}
  </h2>
);

/** Icon tile beside a modal title, as on the Lernkarten heading. */
export const ModalSplitTitleIcon = ({ icon: Icon }: { icon: LucideIcon }) => (
  <span
    aria-hidden="true"
    className="flex size-10 shrink-0 items-center justify-center rounded-xl border-2 border-primary bg-white text-primary shadow-sm"
  >
    <Icon className="size-5" aria-hidden="true" />
  </span>
);

/** Close control floating in the modal's top-right corner. */
export const ModalSplitCloseButton = ({
  onClose,
  label = "Schließen",
}: {
  onClose: () => void;
  /** Accessible name; interactive elements say "Fertig" like their other overlays. */
  label?: string;
}) => (
  <Button
    type="button"
    size="icon-sm"
    variant="ghost"
    aria-label={label}
    onClick={onClose}
    className="absolute right-4 top-4 z-10"
  >
    <CloseIcon />
  </Button>
);
